<template>
	<Transition name="slide-down">
		<div
			key="progress-linear"
			role="progressbar"
			aria-valuemin="0"
			aria-valuemax="100"
			:aria-valuenow="deltaValue"
			ref="progressLinear"
			class="tn-progress-linear"
			:class="classList"
			:style="rootStyle"
			v-if="active"
		>
			<div
				class="tn-progress-linear__background"
				:style="backgroundStyle"
			/>

			<!-- Buffer -->
			<div
				v-show="!indeterminate"
				class="tn-progress-linear__buffer"
				:style="bufferStyle"
			/>

			<!-- Progress -->
			<div
				:class="progressClass + determinateRounded"
				:style="!indeterminate && progressStyle"
			>
				<template v-if="indeterminate">
					<div
						class="long"
						:style="customColor && `background-color: ${customColor}`"
						:class="`${determinateRounded} ${reverse ? 'reverse' : ''} ${alternate ? 'alternate' : ''}`"
					/>
					<div
						v-if="!alternate"
						class="short"
						:style="customColor && `background-color: ${customColor}`"
						:class="`${determinateRounded} ${reverse ? 'reverse' : ''} ${alternate ? 'alternate' : ''}`"
					/>
				</template>
			</div>
		</div>
	</Transition>
</template>

<script>
import variables from "@/assets/tokens/json/variables.json";

/***
 * The TnProgressLinear component is used to convey data visually to users. They can also represent an indeterminate amount, such as loading or processing.
 * Example use-cases include showing mobile data usage or point summary of TV subscription.
 * @displayName TnProgressLinear
 */
export default defineComponent({
	name: "TnProgressLinear",

	props: {
		/**
		 * Hides the component if set to false
		 */
		active: { type: Boolean, default: true },
		/**
		 * Alternate the indeterminate animation
		 */
		alternate: { type: Boolean, default: false },
		/**
		 * Absolute positioning
		 */
		absolute: { type: Boolean, default: false },
		/**
		 * The value of the buffer
		 */
		bufferValue: { type: Number, default: 0 },
		/**
		 * Sets the color scheme.
		 * Choose between 'grey' and 'blue' (will be affected by theme)
		 * or choose custom from all available colors
		 */
		color: { type: String, default: "blue" },
		/**
		 * Change the color of the buffer
		 * Affected by theme
		 * or choose custom
		 */
		feedback: { type: String, default: "info" },
		/**
		 * Sets the height of the progress bar
		 */
		height: { type: String, default: "" },
		/**
		 * 	Constantly animates, use when loading progress is unknown.
		 */
		indeterminate: { type: Boolean, default: false },
		/**
		 * (Coming soon) Makes the bar interactive and emits new value
		 * as input.
		 */
		interactive: { type: Boolean, default: false },
		/**
		 * Reverse the direction of the animation
		 */
		reverse: { type: Boolean, default: false },
		/**
		 * Rounded edges on the bar, progress and buffer.
		 */
		rounded: { type: Boolean },
		/**
		 * Sets the height of the progress bar
		 * Use "height" to override
		 */
		size: { type: String, default: "l" },
		/**
		 * Sets the theme.
		 * Default colors will change acoording to theme.
		 */
		theme: { type: String, default: "white" },
		/**
		 * Changes behaviour
		 */
		threshold: { type: Boolean, default: false },
		/**
		 * The value of progress
		 */
		modelValue: { type: Number, default: 0 },
	},

	// methods: {
	// click(e) {
	//     if (this.interactive) {
	//         e.preventDefault();
	//         this.$emit('input', e.offset / this.$refs.progressLinear.getBoundingClientRect().width);
	//     }
	// }
	// }
	computed: {
		rootStyle() {
			return {
				height: `${this.setSize}px`,
				...(this.interactive ? { cursor: "pointer" } : {}),
			};
		},
		progressStyle() {
			return {
				width: this.indeterminate ? "100%" : `${this.deltaValue}%`,
				...(this.customColor ? { backgroundColor: this.customColor } : {}),
			};
		},
		bufferStyle() {
			return {
				width: `${this.deltaBuffer}%`,
				// ...(this.rounded ? { borderRadius: `${this.setSize}px` } : {}),
				...(this.customColor ? { backgroundColor: this.customColor, opacity: 0.4 } : {}),
			};
		},
		backgroundStyle() {
			return {
				...(this.customColor ? { backgroundColor: this.customColor, opacity: 0.2 } : {}),
			};
		},
		setSize() {
			if (this.height) return this.height;
			else {
				switch (this.size) {
					case "l":
						return "8";
					case "m":
						return "4";
					case "s":
						return "2";
					default:
						return "8";
				}
			}
		},
		thresholdValue() {
			return this.threshold ? this.bufferValue : 0;
		},
		thresholdReached() {
			return this.modelValue >= this.thresholdValue;
		},
		determinateRounded() {
			if (this.threshold && this.rounded) {
				return "";
				// return !this.thresholdReached ? 'tn-progress-linear__rounded' : '';
			} else {
				return "";
				// return this.rounded ? 'tn-progress-linear__rounded' : '';
			}
		},
		progressClass() {
			return [`tn-progress-linear__${this.indeterminate ? "in" : ""}determinate `];
		},
		customColor() {
			return ["blue", "grey"]?.some((color) => color === this.color) ? "" : variables[this.color.trim().toLowerCase()];
		},
		classList() {
			return [
				this.rounded ? `tn-progress-linear__rounded` : "",
				this.absolute ? "tn-progress-linear__absolute" : "",
				["blue", "grey"].some((color) => color === this.color) ? this.color : "",
				`theme-${this.theme}`,
				this.feedback,
			];
		},
		deltaValue() {
			return this.threshold && this.thresholdReached ? this.thresholdValue * 100 : this.modelValue * 100;
		},
		deltaBuffer() {
			return this.threshold ? this.modelValue * 100 : this.bufferValue * 100;
		},
	},
});
</script>

<style lang="scss">
@use "@/assets/scss/variables" as variables;
@use "@/assets/global-style/scss/main" as *;
@use "./style/animations" as *;
@use "./style/colors" as *;
@use "./style/type" as *;

.tn-progress-linear {
	position: relative;
	overflow: hidden;
	width: 100%;
	background: transparent;

	@include colors(linear);

	&.tn-progress-linear__absolute {
		position: absolute;
		left: 0;
		z-index: 1;
	}

	.tn-progress-linear__background {
		position: absolute;
		width: 100%;
		height: 100%;
		bottom: 0;
		left: 0;
		top: 0;
		transition: inherit;
	}
}

.tn-progress-linear__determinate {
	position: absolute;
	transition: width 0.2s cubic-bezier(0.4, 0, 0.6, 1);
	height: 100%;
}

.tn-progress-linear__buffer {
	position: absolute;
	left: 0;
	transition: width 0.2s cubic-bezier(0.4, 0, 0.6, 1);
	height: inherit;
}

.tn-progress-linear__rounded {
	border-radius: 100px;
}

.tn-progress-linear__indeterminate {
	height: inherit;
	transition: all 0.2s cubic-bezier(0.4, 0, 0.6, 1);
	position: relative;

	.long,
	.short {
		width: auto;
		height: 100%;
		position: absolute;
		transition: all 0.2s cubic-bezier(0.4, 0, 0.6, 1);

		&.reverse {
			animation-direction: reverse;
		}
	}

	.long {
		@include animations(long);

		&.alternate {
			@include animations(alternate);
		}
	}

	.short {
		@include animations(short);
	}
}

.labels,
.support {
	display: flex;
	flex-wrap: nowrap;
	justify-content: space-between;
	align-items: center;
	font-family: variables.$font-family-telenor-base;
	color: variables.$color-neutrals-black;

	&.theme-dark {
		color: white;
	}
}

.labels > :first-child {
	font-family: variables.$font-family-telenor-base;
}

.labels {
	margin-bottom: variables.$spacing-s;

	&:empty {
		margin-bottom: 0;
	}

	&.s,
	&.m {
		margin-bottom: variables.$spacing-xs;

		&:empty {
			margin-bottom: 0;
		}
	}
}

.support {
	margin-top: variables.$spacing-s;

	&:empty {
		margin-top: 0;
	}

	&.s,
	&.m {
		margin-top: variables.$spacing-xs;

		&:empty {
			margin-top: 0;
		}
	}
}

.support:empty {
	margin-top: 0;
}

.slide-down-enter-active {
	// .slide-down-leave-active {
	transition: all 0.7s;
	opacity: 1;
}

.slide-down-enter-from
/* .fade-leave-active in <2.1.8 */ {
	opacity: 0;
	transform: translateX(-5px);
}
</style>
